import { trackUserEvent, UserEvent } from '@core/analytics';
import { useAppDispatch } from '@core/redux/hooks';
import { distanceBetweenCoordinates } from '@core/logic';
import { postTransformScan, ScanTransformRequest } from '@scans/components/Scans/helper';
import { getEndpointSnapThreshold, resolveEndpointSnapTarget } from '@scans/components/Scans/snap';
import { scanActions } from '@scans/redux';
import type { Scan } from '@scans/types';
import {
  getTrenchRadiusForViewport,
  isEndPoint,
  TRENCH_POINT_FACTOR,
} from '@trenches/components/Trench/helper';
import { getTrenchesInScope } from '@trenches/components/connect';
import { pointSelectors, trenchSelectors, trenchThunks } from '@trenches/redux';
import type { NewPoint, Point, Trench } from '@trenches/types';
import { eulerToJson, vectorToJson } from '@viewer3D/helper';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import type { VisibleScans } from '@viewer3D/types';
import { useSelector } from 'react-redux';
import { Event, Vector3 } from 'three';
import { TransformationReason } from '@processingResults/components';
import { transformTrenchPointsWithScanState } from '../ScanDetailPanel/state';

interface UserData {
  processingResultId: string;
  scanId: string;
}

export interface Props {
  selectedPoint?: Point;
  selectedPointId?: string;
  selectedTrenchId?: string;
  selectedScan?: Scan;
  visibleScans: VisibleScans;
  selectScansUnderMouseCursor: (e: globalThis.MouseEvent, visibleScanIds: string[]) => void;
  transformScan: (
    event: Event,
  ) => ((reason: TransformationReason, comment: string) => Promise<void>) | undefined;

  resetUserTransformation: (
    scanId: string,
    processingResultId: string,
    body: ScanTransformRequest,
  ) => void;
  resetScanToUserTransformation: (scanId: string) => void;
  createTrench: (
    fromPoint: NewPoint,
    toPoint: NewPoint,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => Promise<unknown>;
  extendOrBranchTrench: (
    point: NewPoint,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => Promise<unknown>;
  extendOrBranchTrenchToPoint: (
    pointId: string,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => Promise<unknown>;
  createTrenchToExistingPoint: (
    fromPoint: NewPoint,
    toPointId: string,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => Promise<unknown>;
  createTrenchFromExistingPoint: (
    fromPointId: string,
    toPoint: NewPoint,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => Promise<unknown>;
  createTrenchBetweenExistingPoints: (
    fromPointId: string,
    toPointId: string,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => Promise<unknown>;
  pointEntities: Record<string, Point | undefined>;
  resolveSnapTarget: (point: NewPoint, excludedPointId?: string) => Point | undefined;
}

export const useConnect = (): Props => {
  const selectedPointId = useSelector(viewer3dSelectors.selectedPointId);
  const selectedPoint = useSelector(pointSelectors.selectById(selectedPointId));
  const selectedTrenchId = useSelector(viewer3dSelectors.selectedTrenchId);
  const selectedScan = useSelector(viewer3dSelectors.selectedScan);
  const selectedTrench = useSelector(trenchSelectors.selectById(selectedTrenchId));
  const projectId = useSelector(viewer3dSelectors.selectedProjectId)!;
  const visibleScans = useSelector(viewer3dSelectors.visibleScans);
  const cameraViewport = useSelector(viewer3dSelectors.cameraViewport);
  const pointEntities = useSelector(pointSelectors.selectEntities);
  const allTrenches = useSelector(trenchSelectors.selectAll);
  const isSelectedPointEndpoint = isEndPoint(selectedPointId, selectedTrench);
  const isSelectedPointFirstPoint = selectedTrench?.pointIds[0] === selectedPointId;
  const trenchesInScope = getTrenchesInScope({
    allTrenches,
    pointEntities,
    selectedProjectId: projectId,
    selectedTrenchId,
    viewport: cameraViewport,
  });
  const pointRadius = getTrenchRadiusForViewport(TRENCH_POINT_FACTOR, cameraViewport);
  const dispatch = useAppDispatch();

  const createTrenchForProject = async (
    projectId: string,
    fromPoint: NewPoint,
    toPoint: NewPoint,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => {
    const { payload } = await dispatch(
      trenchThunks.createTrench({
        path: ['projects', projectId, 'trenches'],
        body: { projectId, fromPoint, toPoint, prelabelId, width: prelabeledWidth },
      }),
    );

    return payload as Trench;
  };

  const createTrenchToExistingPointForProject = async (
    projectId: string,
    fromPoint: NewPoint,
    toPointId: string,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => {
    const { payload } = await dispatch(
      trenchThunks.createTrench({
        path: ['projects', projectId, 'trenches'],
        body: { projectId, fromPoint, toPointId, prelabelId, width: prelabeledWidth },
      }),
    );

    return payload as Trench;
  };

  const extendTrench = async (
    selectedTrenchId: string,
    fromPointId: string,
    newPoint: NewPoint,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => {
    const { payload } = await dispatch(
      trenchThunks.extendTrench({
        path: ['projects', projectId, 'trenches', selectedTrenchId, 'extend'],
        body: {
          fromPointId,
          newPoint,
          prelabelId,
          width: prelabeledWidth,
        },
      }),
    );
    return payload as Trench;
  };

  const extendTrenchToPointId = async (
    selectedTrenchId: string,
    fromPointId: string,
    newPointId: string,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => {
    const { payload } = await dispatch(
      trenchThunks.extendTrench({
        path: ['projects', projectId, 'trenches', selectedTrenchId, 'extend'],
        body: {
          fromPointId,
          newPointId,
          prelabelId,
          width: prelabeledWidth,
        },
      }),
    );
    return payload as Trench;
  };

  const branchFromTrench = async (
    projectId: string,
    fromPointId: string,
    toPoint: NewPoint,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => {
    const { payload } = await dispatch(
      trenchThunks.createTrench({
        path: ['projects', projectId, 'trenches'],
        body: {
          fromPointId,
          toPoint,
          projectId,
          prelabelId,
          width: prelabeledWidth,
        },
      }),
    );
    return payload as Trench;
  };

  const branchFromTrenchToPointId = async (
    projectId: string,
    fromPointId: string,
    toPointId: string,
    prelabelId?: string,
    prelabeledWidth?: number,
  ) => {
    const { payload } = await dispatch(
      trenchThunks.createTrench({
        path: ['projects', projectId, 'trenches'],
        body: {
          fromPointId,
          toPointId,
          projectId,
          prelabelId,
          width: prelabeledWidth,
        },
      }),
    );
    return payload as Trench;
  };

  const selectTrench = (trenchId: string) =>
    dispatch(viewer3dActions.setSelectedTrenchId(trenchId));

  const selectPoint = (pointId: string) => dispatch(viewer3dActions.setSelectedPointId(pointId));

  return {
    selectedPoint,
    selectedPointId,
    selectedScan,
    selectedTrenchId,
    visibleScans,
    pointEntities,

    resetUserTransformation: async (
      scanId: string,
      processingResultId: string,
      body: ScanTransformRequest,
    ) => {
      // has backend request
      await postTransformScan(dispatch, scanId, projectId, processingResultId, body);
    },

    transformScan: ({ target }) => {
      interface TransformTarget {
        object?: import('three').Object3D;
        rotationAngle: number;
        rotationAxis: Vector3;
        worldPosition: Vector3;
        worldPositionStart: Vector3;
        mode: string;
      }
      const controls = target as unknown as TransformTarget;
      const transformObject = controls.object;
      if (!transformObject) return;
      const [scan] = transformObject.children;
      if (!scan) return;
      const { processingResultId, scanId } = (scan.userData as UserData) ?? {};
      if (!processingResultId) return;

      const userRotation = transformObject.rotation.clone();
      // we apply the userRotation, to make the translations saved in the db consistent with processing results
      // from our processing pipeline
      const userTranslation = transformObject.position
        .clone()
        .add(scan.position.clone().applyEuler(userRotation));

      const {
        rotationAngle: angle,
        rotationAxis: axis,
        worldPosition,
        worldPositionStart,
        mode,
      } = controls;

      const processingResult = {
        userTranslation: vectorToJson(userTranslation),
        userRotation: eulerToJson(userRotation),
      };
      const points = {
        userTranslation: vectorToJson(worldPosition),
        userRotation:
          mode === 'rotate'
            ? {
                angle,
                axis: vectorToJson(axis),
              }
            : {
                angle: 0,
                axis: vectorToJson(new Vector3(0, 0, 0)),
              },
        transformOrigin: vectorToJson(worldPositionStart),
      };
      const { applyTransformation: transformAllPointsCheckbox } =
        transformTrenchPointsWithScanState;

      const body = transformAllPointsCheckbox ? { processingResult, points } : { processingResult };

      return async (reason: TransformationReason, comment: string) => {
        await postTransformScan(dispatch, scanId, projectId, processingResultId, {
          ...body,
          processingResult: {
            ...processingResult,
            reason,
            comment,
          },
          reason,
          comment,
        });
      };
    },

    selectScansUnderMouseCursor: ({ clientX, clientY }, visibleScanIds) => {
      const scanId = visibleScanIds[0];
      dispatch(viewer3dActions.clearSelection({ keepCamera: true }));
      dispatch(viewer3dActions.setSelectedScanId({ id: scanId, focus: false }));
      if (visibleScanIds.length < 2) return;
      dispatch(
        viewer3dActions.setScanSelection({
          left: clientX,
          top: clientY,
          scanIds: visibleScanIds,
        }),
      );
    },

    resetScanToUserTransformation: (scanId) => {
      /** just client, no persistence */
      dispatch(
        scanActions.updateOne({
          id: scanId,
          disableUserTransformation: false,
          disableAutoAlignedPosition: true,
        }),
      );
    },

    extendOrBranchTrench: async (point, prelabelId, prelabeledWidth) => {
      if (!selectedTrenchId || !selectedPointId || !selectedPoint) {
        throw new Error('No trench or point selected!');
      }
      const distanceExtendedOrBranched = distanceBetweenCoordinates(selectedPoint, point);
      const { pointIds, id } = isSelectedPointEndpoint
        ? await extendTrench(selectedTrenchId, selectedPointId, point, prelabelId, prelabeledWidth)
        : await branchFromTrench(projectId, selectedPointId, point, prelabelId, prelabeledWidth);

      trackUserEvent(UserEvent.TrenchMeterLabeled, {
        labeledMeters: distanceExtendedOrBranched,
        projectId,
      });

      if (pointIds.length > 0) {
        const nextSelectedPointId = pointIds[isSelectedPointFirstPoint ? 0 : pointIds.length - 1];
        selectPoint(nextSelectedPointId);
        if (nextSelectedPointId) selectTrench(id);
      }
    },

    extendOrBranchTrenchToPoint: async (pointId, prelabelId, prelabeledWidth) => {
      if (!selectedTrenchId || !selectedPointId || !selectedPoint) {
        throw new Error('No trench or point selected!');
      }
      const point = pointEntities[pointId];
      if (!point) {
        throw new Error(`Point ${pointId} not found`);
      }
      const distanceExtendedOrBranched = distanceBetweenCoordinates(selectedPoint, point);
      const { pointIds, id } = isSelectedPointEndpoint
        ? await extendTrenchToPointId(
            selectedTrenchId,
            selectedPointId,
            pointId,
            prelabelId,
            prelabeledWidth,
          )
        : await branchFromTrenchToPointId(
            projectId,
            selectedPointId,
            pointId,
            prelabelId,
            prelabeledWidth,
          );

      trackUserEvent(UserEvent.TrenchMeterLabeled, {
        labeledMeters: distanceExtendedOrBranched,
        projectId,
      });

      if (pointIds.length > 0) {
        const nextSelectedPointId = pointIds[isSelectedPointFirstPoint ? 0 : pointIds.length - 1];
        selectPoint(nextSelectedPointId);
        if (nextSelectedPointId) selectTrench(id);
      }
    },

    createTrench: async (fromPoint, toPoint, prelabelId, prelabeledWidth) => {
      const { pointIds, id } = await createTrenchForProject(
        projectId,
        fromPoint,
        toPoint,
        prelabelId,
        prelabeledWidth,
      );
      if (pointIds.length > 0) selectPoint(pointIds[pointIds.length - 1]);
      selectTrench(id);
      trackUserEvent(UserEvent.TrenchMeterLabeled, {
        labeledMeters: distanceBetweenCoordinates(fromPoint, toPoint),
        projectId,
      });
    },

    createTrenchToExistingPoint: async (fromPoint, toPointId, prelabelId, prelabeledWidth) => {
      const toPoint = pointEntities[toPointId];
      if (!toPoint) {
        throw new Error(`Point ${toPointId} not found`);
      }

      const { pointIds, id } = await createTrenchToExistingPointForProject(
        projectId,
        fromPoint,
        toPointId,
        prelabelId,
        prelabeledWidth,
      );
      if (pointIds.length > 0) {
        const nextSelectedPointId = pointIds[pointIds.length - 1];
        selectPoint(nextSelectedPointId);
        if (nextSelectedPointId) selectTrench(id);
      }
      trackUserEvent(UserEvent.TrenchMeterLabeled, {
        labeledMeters: distanceBetweenCoordinates(fromPoint, toPoint),
        projectId,
      });
    },

    createTrenchFromExistingPoint: async (fromPointId, toPoint, prelabelId, prelabeledWidth) => {
      const fromPoint = pointEntities[fromPointId];
      if (!fromPoint) {
        throw new Error(`Point ${fromPointId} not found`);
      }

      const { pointIds, id } = await branchFromTrench(
        projectId,
        fromPointId,
        toPoint,
        prelabelId,
        prelabeledWidth,
      );
      if (pointIds.length > 0) {
        const nextSelectedPointId = pointIds[pointIds.length - 1];
        selectPoint(nextSelectedPointId);
        if (nextSelectedPointId) selectTrench(id);
      }
      trackUserEvent(UserEvent.TrenchMeterLabeled, {
        labeledMeters: distanceBetweenCoordinates(fromPoint, toPoint),
        projectId,
      });
    },

    createTrenchBetweenExistingPoints: async (
      fromPointId,
      toPointId,
      prelabelId,
      prelabeledWidth,
    ) => {
      const fromPoint = pointEntities[fromPointId];
      const toPoint = pointEntities[toPointId];
      if (!fromPoint || !toPoint) {
        throw new Error(`Points ${fromPointId} or ${toPointId} not found`);
      }

      const { pointIds, id } = await branchFromTrenchToPointId(
        projectId,
        fromPointId,
        toPointId,
        prelabelId,
        prelabeledWidth,
      );
      if (pointIds.length > 0) {
        const nextSelectedPointId = pointIds[pointIds.length - 1];
        selectPoint(nextSelectedPointId);
        if (nextSelectedPointId) selectTrench(id);
      }
      trackUserEvent(UserEvent.TrenchMeterLabeled, {
        labeledMeters: distanceBetweenCoordinates(fromPoint, toPoint),
        projectId,
      });
    },

    resolveSnapTarget: (point, excludedPointId) => {
      const candidate = resolveEndpointSnapTarget({
        clickPoint: point,
        excludedPointId,
        pointEntities,
        selectedPointId,
        threshold: getEndpointSnapThreshold(pointRadius),
        trenchesInScope,
      });

      return candidate ? pointEntities[candidate.pointId] : undefined;
    },
  };
};
